import { Content, List, Root, Trigger } from '@radix-ui/react-tabs';
import { forwardRef, type ComponentPropsWithoutRef, type ElementRef } from 'react';

import { cn } from '@/shared/utils/cn/cn.util';

export const Tabs = Root;

export type TabsListProps = ComponentPropsWithoutRef<typeof List>;
export type TabsTriggerProps = ComponentPropsWithoutRef<typeof Trigger>;
export type TabsContentProps = ComponentPropsWithoutRef<typeof Content>;

/**
 * Radix reads the resolved direction from its `DirectionProvider` (mounted in
 * `shared/theme/DirectionProvider`), so ArrowRight moves to the *previous* tab
 * in Arabic without a single conditional here.
 */
export const TabsList = forwardRef<ElementRef<typeof List>, TabsListProps>(function TabsList(
  { className, ...props },
  ref,
) {
  return (
    <List
      ref={ref}
      className={cn(
        'inline-flex h-10 items-center justify-center gap-xs rounded-md bg-muted p-1 text-muted-foreground',
        className,
      )}
      {...props}
    />
  );
});

export const TabsTrigger = forwardRef<ElementRef<typeof Trigger>, TabsTriggerProps>(
  function TabsTrigger({ className, ...props }, ref) {
    return (
      <Trigger
        ref={ref}
        className={cn(
          'inline-flex items-center justify-center gap-sm whitespace-nowrap rounded-sm px-md py-1.5 text-label font-medium transition-all duration-fast ease-standard',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
          'disabled:pointer-events-none disabled:opacity-50',
          'data-[state=active]:bg-card data-[state=active]:text-foreground data-[state=active]:shadow-sm',
          className,
        )}
        {...props}
      />
    );
  },
);

/**
 * `data-[state=inactive]:hidden` is what makes `forceMount` usable at all.
 *
 * Radix hides an inactive panel by **not rendering it**; under `forceMount` it
 * renders it with `data-state="inactive"`, no `hidden` attribute, and **no
 * hiding of any kind** — so every force-mounted panel paints at once, stacked,
 * all of them in the tab order. The rule belongs here rather than at each call
 * site because a caller who force-mounts is asking for the panel to *survive*,
 * never for it to be *visible*, and the second half is invisible to forget:
 * nothing warns, and it looks like a duplicated component.
 */
export const TabsContent = forwardRef<ElementRef<typeof Content>, TabsContentProps>(
  function TabsContent({ className, ...props }, ref) {
    return (
      <Content
        ref={ref}
        className={cn(
          'mt-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
          'data-[state=inactive]:hidden',
          className,
        )}
        {...props}
      />
    );
  },
);
